import { get, post, del } from './api';

export type GameResponse = {
  join_code: string;
  state: string;
  moderator: string;
  created_at: string;
  weapon_mode: boolean;
};

export type WeaponResponse = {
  id: number;
  name: string;
  description: string;
  status: 'approved' | 'pending' | 'declined';
  suggested_by: string | null;
};
export type WeaponBriefResponse = {
  // weapon info returned alongside players target
  name: string;
  description: string;
};

export type TargetResponse = {
  // weapon is null when the game is not in weapon_mode
  target: string | null;
  weapon: WeaponBriefResponse | null;
};

export type WeaponTemplateResponse = {
  id: number;
  name: string;
  description: string;
  difficulty: 'easy' | 'moderate' | 'hard';
  location: 'anywhere' | 'cabin' | 'university' | 'travel';
  language: 'en' | 'no';
};

export type GameDetailResponse = GameResponse & {
  members: { name: string; status: string }[];
  winner: string | null;
};

export type MembershipResponse = {
  player: string;
  status: string;
  token: string;
};

export type GameEvent = {
  type: string;
  happened_at: string;
  actor: string | null;
  target: string | null;
  meta: Record<string, unknown>;
};

export type PodiumEntryResponse = {
  name: string;
  status: string;
  kills: number;
  eliminated_at: string | null;
  weapons_collected: string[];
};

export type PodiumResponse = {
  last_standing: string | null;
  most_kills: string | null;
  gods_favorite: string | null;
  active_count: number;
  player_count: number;
  kill_ranking: PodiumEntryResponse[];
  death_ranking: PodiumEntryResponse[];
};

// Game

export function fetchGame(code: string) {
  return get<GameDetailResponse>(`/games/${encodeURIComponent(code)}/`);
}

export function fetchEvents(code: string, after?: string) {
  const qs = after ? `?after=${encodeURIComponent(after)}` : '';
  return get<GameEvent[]>(`/games/${encodeURIComponent(code)}/events/${qs}`);
}

export function createGame(moderatorName: string, pin: string, weaponMode: boolean) {
  return post<GameResponse & { token: string }>('/games/', {
    moderator_name: moderatorName,
    pin,
    weapon_mode: weaponMode,
  });
}

export function startGame(code: string, moderatorName: string) {
  return post<void>(`/games/${code}/start/`, { moderator_name: moderatorName });
}

export function joinGame(code: string, playerName: string, pin: string) {
  return post<MembershipResponse>('/games/join/', { code, player_name: playerName, pin });
}

export function rejoinGame(code: string, playerName: string, pin: string) {
  return post<MembershipResponse>('/games/rejoin/', { code, player_name: playerName, pin });
}

export function leaveGame(code: string, playerName: string, token: string) {
  return post<void>(`/games/${code}/me/leave/`, { player_name: playerName, token });
}

export function cancelGame(code: string, playerName: string) {
  return post<void>(`/games/${code}/cancel/`, { player_name: playerName });
}

export function restartGame(code: string, moderatorName: string, weaponMode: boolean) {
  return post<GameDetailResponse>(`/games/${code}/restart/`, {
    moderator_name: moderatorName,
    weapon_mode: weaponMode,
  });
}

export function kickPlayer(code: string, moderatorName: string, playerName: string) {
  return del<void>(`/games/${code}/players/${encodeURIComponent(playerName)}/`, {
    moderator_name: moderatorName,
  });
}

export function myTarget(code: string, playerName: string, token: string) {
  return post<TargetResponse>(`/games/${code}/me/target/`, {
    player_name: playerName,
    token,
  });
}

export function requestKill(code: string, playerName: string, token: string) {
  return post<{ request_id: number }>(`/games/${code}/me/kill/request/`, {
    player_name: playerName,
    token,
  });
}

export function confirmKill(code: string, playerName: string, token: string, requestId: number) {
  return post<{ target: string | null }>(`/games/${code}/me/kill/confirm/`, {
    player_name: playerName,
    token,
    request_id: requestId,
  });
}

export function declineKill(code: string, playerName: string, token: string, requestId: number) {
  return post<void>(`/games/${code}/me/kill/decline/`, {
    player_name: playerName,
    token,
    request_id: requestId,
  });
}

// Weapon Pool

// moderator only
export function fetchWeapons(code: string, moderatorName: string) {
  return get<WeaponResponse[]>(
    `/games/${code}/weapons/?moderator_name=${encodeURIComponent(moderatorName)}`
  );
}

// moderator only
export function addWeapon(
  code: string,
  moderatorName: string,
  weaponName: string,
  description = ''
) {
  return post<WeaponResponse>(`/games/${code}/weapons/`, {
    moderator_name: moderatorName,
    weapon_name: weaponName,
    description,
  });
}

// suggested weapon lands in "pending" status; moderator must approve/decline it.
export function suggestWeapon(
  code: string,
  playerName: string,
  token: string,
  weaponName: string,
  description = ''
) {
  return post<WeaponResponse>(`/games/${code}/weapons/suggest/`, {
    player_name: playerName,
    token,
    weapon_name: weaponName,
    description,
  });
}

// moderator only
export function moderateWeapon(
  code: string,
  moderatorName: string,
  weaponId: number,
  action: 'approve' | 'decline'
) {
  return post<void>(`/games/${code}/weapons/${weaponId}/moderate/`, {
    moderator_name: moderatorName,
    action,
  });
}

// moderator only
export function removeWeapon(code: string, moderatorName: string, weaponId: number) {
  return del<void>(`/games/${code}/weapons/${weaponId}/`, { moderator_name: moderatorName });
}

// Weapon templates — global inventory

export function fetchWeaponTemplates(filters?: {
  difficulty?: string;
  location?: string;
  language?: string;
}) {
  const qs = new URLSearchParams();
  if (filters?.difficulty) qs.set('difficulty', filters.difficulty);
  if (filters?.location) qs.set('location', filters.location);
  if (filters?.language) qs.set('language', filters.language);
  const query = qs.toString() ? `?${qs}` : '';
  return get<WeaponTemplateResponse[]>(`/weapon-templates/${query}`);
}

// moderator only
export function importTemplates(code: string, moderatorName: string, templateIds: number[]) {
  return post<void>(`/games/${code}/weapons/import/`, {
    moderator_name: moderatorName,
    template_ids: templateIds,
  });
}

// Podium

export function fetchPodium(code: string) {
  return get<PodiumResponse>(`/games/${code}/podium/`);
}
